import * as React from "react";
import { Permission, Role, WebPortalClient } from "webportalclient";
import DocumentTitle from "../../misc/components/DocumentTitle";
import Collapsible from 'react-collapsible';
import '../../misc/Collapsible.scss';
import { Link, useParams } from "react-router-dom";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { AuditModal } from "../../auditmodal/components/AuditModal";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

type CheckablePermission = { id: string; name: string; description: string; checked: boolean; category: string }

export const AlignPermissionsToRole = () => {
    const client = useWebPortalApiClient();
    const { id } = useParams<{ id: string }>();
    const [permissionCategories, setPermissionCategories] = React.useState<{ permissions: CheckablePermission[] }>();
    const [role, setRole] = React.useState<Role>();
    const [submitting, setSubmitting] = React.useState(false);
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [showAuditModal, setShowAuditModal] = React.useState(false);

    React.useEffect(() => {
        const getData = async () => {
            const roleResult = await client.client.getRole(id, undefined, 'permissions');
            const permissionCategoriesResult = await client.client.getPermissionCategories(undefined, undefined, 'displayOrder', 'permissions');

            setRole(roleResult);

            setPermissionCategories({
                permissions: permissionCategoriesResult.value.map(pc => pc.permissions.filter(p => !p.isDisabled).map(p => ({
                    category: pc.description,
                    id: p.id,
                    name: p.name,
                    description: p.description,
                    checked: roleResult.permissionsIds.find(r => r === p.id) ? true : false
                }))).flat()
            });
        }
        getData();
    }, [client.client, id]);

    if (!permissionCategories)
        return <LoadingWithMessage message="Loading Permissions..." />

    const handleSubmit = (annotation: string) => {
        setSuccessAlert(false);
        setSubmitting(true);
        const patch = async () => {
            const permissionIds = permissionCategories.permissions.filter(p => p.checked).map(p => p.id);

            await client.client.patchRole({
                permissionsIds: permissionIds
            }, id, annotation);
            setSubmitting(false);
            setSuccessAlert(true);
            setShowAuditModal(false);
        }

        patch();
    }

    const handleAuditSubmit = () => {
        setSuccessAlert(false);
        setShowAuditModal(true);
    }

    const handleChecked = (event) => {
        const scrollY = window.scrollY;
        setPermissionCategories({
            ...permissionCategories,
            permissions: permissionCategories.permissions.map(pc => {
                if (pc.id === event.target.id)
                    return {
                        ...pc,
                        checked: event.target.checked
                    }
                return pc;
            })
        });
        // Restore scroll after render to prevent jumping to the category head.
        setTimeout(() => {
            window.scrollTo(0, scrollY);
        }, 0);
    }

    return (
        <DocumentTitle title="Align Permissions To Role | Vitalograph Web Portal">
            <form>
                <AlignPermissionsToRoleForm
                    role={role}
                    permissionCategories={permissionCategories.permissions}
                    handleChecked={handleChecked}
                    submitting={submitting}
                    successAlert={successAlert}
                    handleSubmit={handleAuditSubmit}
                />
            </form>
            {showAuditModal ? <AuditModal header="Align Permissions To Role" isSubmitting={submitting} onClose={() => setShowAuditModal(false)} onSubmit={(auditModal) => handleSubmit(auditModal.Annotation)} /> : null}
        </DocumentTitle>
    );
}

interface PermissionItemProps {
    permission: Permission & { checked: boolean };
}

interface ActionButtonsProps {
    submitting: boolean;
    automationIdPrefix?: string;
    onSaveClick: () => void;
}

interface AlignPermissionsToRoleFormProps {
    role: Role;
    permissionCategories: CheckablePermission[];
    handleChecked: (event) => void;
    submitting: boolean;
    successAlert: boolean;
    handleSubmit: () => void;
}

export const AlignPermissionsToRoleForm = ({ role, permissionCategories, handleChecked, submitting, successAlert, handleSubmit }: AlignPermissionsToRoleFormProps) => {
    const ActionButtons = ({ submitting, automationIdPrefix, onSaveClick }: ActionButtonsProps) => {
        return (
            <div className="h2 pull-right">
                <button className="btn btn-primary" type="button" disabled={submitting} onClick={onSaveClick} data-automationid={`${automationIdPrefix}_Save`}>
                    Save
                </button>&nbsp;
                <Link className="btn btn-primary btn-outline" to="/rolemanagement" data-automationid={`${automationIdPrefix}_ReturnToList`}>
                    Return to List
                </Link>
            </div>
        );
    }

    const PermissionItem = ({ permission }: PermissionItemProps) => {
        return (
            <tr id={permission.id}>
                <td key={`PermissionItem${permission.id}Name`}>
                    <label htmlFor={permission.id}>{permission.name}</label>
                </td>
                <td key={`PermissionItem${permission.id}Description`}>
                    {permission.description}
                </td>
                <td key={`PermissionItem${permission.id}Checkbox`} className="col-md-1">
                    <input id={permission.id} name={permission.id} type="checkbox" checked={permission.checked} onChange={handleChecked} />
                </td>
            </tr>
        );
    }

    const groupedPermissionCategories = permissionCategories.reduce((r, v, i, a, k = v.category) => ((r[k] || (r[k] = [])).push(v), r), {});

    const categoryList = () => {
        const trigger = (description) => (
            <h4 id={description}>{description}</h4>
        );

        return Object.keys(groupedPermissionCategories).map(key => (
            <Collapsible key={`Collapsible${key}`} trigger={trigger(key)} open={true}>
                <table key={`Table${key}`} className="table table-striped table-bordered table-hover">
                    <tbody>
                        {groupedPermissionCategories[key].map((p) => (
                            <PermissionItem key={`PermissionItem${p.id}`} permission={p} />
                        ))}
                    </tbody>
                </table>
            </Collapsible>
        ))
    }

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/rolemanagement`}>Role Management list</Link>?
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Align Permissions To Role - {role.name}</h2>
                </div>
                <div className="col-md-6">
                    <ActionButtons submitting={submitting} onSaveClick={() => handleSubmit()} automationIdPrefix="ActionsHeader" />
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div id="alignPermissionsWrapper" className="panel-body">
                            <div className="row">
                                <div className="col-md-12">
                                    {categoryList()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md-12">
                <ActionButtons submitting={submitting} onSaveClick={() => handleSubmit()} automationIdPrefix="ActionsFooter" />
            </div>
        </>
    );
}
